import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import {
  Aperture,
  ArrowLeft,
  ArrowClockwise,
  BookOpen,
  CheckCircle,
  CaretDown,
  DownloadSimple,
  Export,
  FloppyDisk,
  GridFour,
  HardDrive,
  ImageSquare,
  Plus,
  Sparkle,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { AlbumPreview } from "./AlbumPreview";
import { SortablePhoto } from "./SortablePhoto";
import { buildAlbumDocument, DEFAULT_MEDIA_PRESENTATION, makeId, type FramePreset, type FrameSpacing, type MediaFit, type PhotoFolioProject, type PhotoItem, type TemplateId } from "../domain/project";
import { useAssetUrls } from "../hooks/useAssetUrls";
import { useAutosave } from "../hooks/useAutosave";
import { importPhoto } from "../media/process";
import { db } from "../storage/database";
import { deleteAssetFiles } from "../storage/asset-store";
import { readAssetFile } from "../storage/asset-store";
import { createOfflineHtml } from "../exporters/html-exporter";
import { createProjectArchive } from "../exporters/project-exporter";
import { downloadBlob, safeFilename } from "../exporters/download";

interface EditorProps {
  initialProject: PhotoFolioProject;
  onBack: () => void;
  onProjectChange: (project: PhotoFolioProject) => void;
}

const templates: { id: TemplateId; name: string; description: string; experimental?: boolean }[] = [
  { id: "paper", name: "纸上影集", description: "大留白与单幅叙事" },
  { id: "timeline", name: "时间札记", description: "按顺序阅读旅程" },
  { id: "gallery", name: "沉浸画廊", description: "紧凑浏览更多照片" },
  { id: "book", name: "翻页影集", description: "像翻一本真正的纸质相册" },
  { id: "cinema", name: "电影叙事", description: "随滚动展开一段记忆" },
  { id: "deck", name: "记忆卡片", description: "像翻阅一叠照片" },
  { id: "focus", name: "专注画廊", description: "沉浸放大每一张照片" },
  { id: "scrapbook", name: "拼贴手账", description: "照片、文字与纸张拼在一起" },
  { id: "accordion", name: "手风琴图库", description: "展开一张照片，再遇见下一张" },
  { id: "drift", name: "漂移墙", description: "让五张以上照片缓慢漂移" },
  { id: "circular", name: "环形图库", description: "拖动照片在空间中旋转", experimental: true },
  { id: "depth", name: "活照片", description: "指针倾斜与分层视差", experimental: true },
];

const coreTemplates = templates.filter((template) => !template.experimental);
const experimentalTemplates = templates.filter((template) => template.experimental);

type ImportTaskStatus = "queued" | "processing" | "success" | "error";

interface ImportTask {
  id: string;
  file: File;
  name: string;
  status: ImportTaskStatus;
  message?: string;
}

interface StorageSnapshot {
  usage: number;
  quota: number;
  persisted: boolean | null;
}

const formatSavedTime = (value: string) => {
  if (!value) return "尚未保存";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
};

function ImportQueuePanel({ tasks, disabled, onRetry, onClear }: {
  tasks: ImportTask[];
  disabled: boolean;
  onRetry: (task: ImportTask) => void;
  onClear: () => void;
}) {
  const completed = tasks.filter((task) => task.status === "success").length;
  const failures = tasks.filter((task) => task.status === "error").length;
  return (
    <section className="import-queue" aria-live="polite" aria-label="照片导入进度">
      <div className="import-queue-head">
        <div>
          <strong>{failures ? `${failures} 张需要处理` : `正在整理 ${completed}/${tasks.length} 张照片`}</strong>
          <span>{failures ? "可以逐张重试，不会影响已完成的照片。" : "照片会在本地生成预览和缩略图。"}</span>
        </div>
        <button type="button" className="text-button compact" onClick={onClear} disabled={disabled}>清除记录</button>
      </div>
      <div className="import-task-list">
        {tasks.map((task) => (
          <div className={`import-task ${task.status}`} key={task.id}>
            <span className="import-task-state" aria-hidden="true">
              {task.status === "success" ? <CheckCircle size={16} weight="fill" /> : task.status === "error" ? <WarningCircle size={16} weight="fill" /> : task.status === "processing" ? <FloppyDisk size={16} /> : <span className="import-task-dot" />}
            </span>
            <span className="import-task-name" title={task.name}>{task.name}</span>
            <span className="import-task-label">{task.status === "success" ? "已完成" : task.status === "processing" ? "处理中" : task.status === "error" ? "失败" : "排队中"}</span>
            {task.status === "error" ? <button type="button" className="import-retry" onClick={() => onRetry(task)} disabled={disabled}><ArrowClockwise size={14} />重试</button> : null}
          </div>
        ))}
      </div>
    </section>
  );
}

export function Editor({ initialProject, onBack, onProjectChange }: EditorProps) {
  const [project, setProject] = useState(initialProject);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [importQueue, setImportQueue] = useState<ImportTask[]>([]);
  const [storage, setStorage] = useState<StorageSnapshot | null>(null);
  const [mobileMode, setMobileMode] = useState<"settings" | "organize" | "preview">("organize");
  const [exportOpen, setExportOpen] = useState(false);
  const [estimatedBytes, setEstimatedBytes] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const urls = useAssetUrls(project.id, project.items, "preview");
  const thumbUrls = useAssetUrls(project.id, project.items, "thumbnail");
  const document = useMemo(() => buildAlbumDocument(project), [project]);
  const handleSaved = useCallback((saved: PhotoFolioProject) => {
    setProject((current) => current.updatedAt === saved.updatedAt ? current : saved);
    onProjectChange(saved);
  }, [onProjectChange]);
  const { state: saveState, retry: retrySave } = useAutosave(project, handleSaved);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const refreshStorage = useCallback(async () => {
    let estimate: StorageEstimate | undefined;
    try {
      estimate = await navigator.storage?.estimate?.();
    } catch {
      return;
    }
    if (typeof estimate?.usage !== "number" || typeof estimate.quota !== "number") return;
    let persisted: boolean | null = null;
    try {
      persisted = navigator.storage?.persisted ? await navigator.storage.persisted() : null;
    } catch {
      persisted = null;
    }
    setStorage({ usage: estimate.usage, quota: estimate.quota, persisted });
  }, []);

  useEffect(() => {
    refreshStorage();
  }, [refreshStorage]);

  const patchProject = (patch: Partial<PhotoFolioProject>) => {
    setProject((current) => ({ ...current, ...patch }));
  };

  const updateItem = (id: string, patch: Partial<PhotoItem>) => {
    setProject((current) => ({
      ...current,
      items: current.items.map((item) => item.id === id ? { ...item, ...patch } : item),
    }));
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setProject((current) => {
      const from = current.items.findIndex((item) => item.id === active.id);
      const to = current.items.findIndex((item) => item.id === over.id);
      return { ...current, items: arrayMove(current.items, from, to).map((item, order) => ({ ...item, order })) };
    });
  };

  const updateImportTask = (id: string, patch: Partial<ImportTask>) => {
    setImportQueue((current) => current.map((task) => task.id === id ? { ...task, ...patch } : task));
  };

  const processImportTask = async (task: ImportTask, order: number) => {
    updateImportTask(task.id, { status: "processing", message: undefined });
    try {
      const result = await importPhoto(project.id, task.file, order);
      updateImportTask(task.id, { status: "success" });
      return result.item;
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : `${task.name} 导入失败`;
      updateImportTask(task.id, { status: "error", message });
      return null;
    }
  };

  const handleFiles = async (files: FileList | File[]) => {
    if (importing) return;
    const selected = Array.from(files);
    if (!selected.length) return;
    const tasks: ImportTask[] = selected.map((file) => ({ id: makeId("import"), file, name: file.name, status: "queued" }));
    setImportQueue(tasks);
    setImporting(true);
    setError("");
    const imported: PhotoItem[] = [];
    setNotice(`准备整理 ${tasks.length} 张照片`);
    for (const [index, task] of tasks.entries()) {
      setNotice(`正在处理第 ${index + 1}/${tasks.length} 张照片`);
      const item = await processImportTask(task, project.items.length + imported.length);
      if (item) imported.push(item);
    }
    if (imported.length) {
      setProject((current) => ({
        ...current,
        items: [...current.items, ...imported],
        coverAssetId: current.coverAssetId || imported[0].assetId,
      }));
      setNotice(`已导入 ${imported.length} 张照片${tasks.length > imported.length ? `，${tasks.length - imported.length} 张失败` : ""}`);
    }
    if (!imported.length) setError("没有照片导入成功，请检查下方任务并重试");
    setImporting(false);
    await refreshStorage();
  };

  const retryImport = async (task: ImportTask) => {
    if (importing) return;
    setImporting(true);
    setError("");
    const item = await processImportTask(task, project.items.length);
    if (item) {
      setProject((current) => {
        const nextItem = { ...item, order: current.items.length };
        return { ...current, items: [...current.items, nextItem], coverAssetId: current.coverAssetId || nextItem.assetId };
      });
      setNotice(`${task.name} 已重新导入`);
    } else {
      setError(task.message || `${task.name} 重试失败`);
    }
    setImporting(false);
    await refreshStorage();
  };

  const requestPersistence = async () => {
    if (!navigator.storage?.persist) return setNotice("当前浏览器不支持持久化存储请求");
    try {
      const granted = await navigator.storage.persist();
      await refreshStorage();
      setNotice(granted ? "浏览器已尽量保留这个本地项目" : "浏览器暂未批准持久化，请下载草稿备份");
    } catch {
      setNotice("无法读取持久化状态，请下载草稿备份");
    }
  };

  const removeItem = async (item: PhotoItem) => {
    await Promise.all([deleteAssetFiles(project.id, item.assetId), db.assets.delete(item.assetId)]);
    setProject((current) => {
      const items = current.items.filter((entry) => entry.id !== item.id).map((entry, order) => ({ ...entry, order }));
      return { ...current, items, coverAssetId: current.coverAssetId === item.assetId ? items[0]?.assetId : current.coverAssetId };
    });
  };

  const exportHtml = async () => {
    if (!project.items.length) return setError("请先导入至少一张照片");
    setNotice("正在生成离线网页");
    try {
      const html = await createOfflineHtml(project);
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      downloadBlob(blob, `${safeFilename(project.title)}.html`);
      setNotice(`离线网页已生成，约 ${(blob.size / 1024 / 1024).toFixed(1)} MB`);
      setExportOpen(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "导出失败");
    }
  };

  const calculateEstimate = async (quality = project.exportQuality) => {
    setEstimatedBytes(null);
    if (!project.items.length) return;
    try {
      const blobs = await Promise.all(project.items.map((item) => readAssetFile(
        project.id,
        item.assetId,
        quality === "archive" ? "original" : "preview",
      )));
      const raw = blobs.reduce((sum, blob) => sum + blob.size, 0);
      setEstimatedBytes(Math.round(raw * (quality === "light" ? .75 : 1.34) + 180_000));
    } catch {
      setEstimatedBytes(null);
    }
  };

  const openExportCenter = async () => {
    setExportOpen(true);
    await calculateEstimate();
  };

  const exportDraft = async () => {
    if (!project.items.length) return setError("空项目不需要备份");
    setNotice("正在整理项目草稿");
    try {
      const archive = await createProjectArchive(project);
      downloadBlob(archive, `${safeFilename(project.title)}.photofolio`);
      setNotice("项目草稿已下载");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "草稿导出失败");
    }
  };

  return (
    <main className={`editor-shell mobile-${mobileMode}`}>
      <header className="editor-topbar">
        <button className="topbar-back" type="button" onClick={onBack}><ArrowLeft size={19} />所有影集</button>
        <div className="topbar-title"><strong>{project.title}</strong><span className={`save-state ${saveState}`}>
          {saveState === "saved" ? <CheckCircle size={15} /> : saveState === "saving" ? <FloppyDisk size={15} /> : <WarningCircle size={15} />}
          {saveState === "saved" ? "已保存到此浏览器" : saveState === "saving" ? "正在保存" : "保存失败"}
        </span></div>
        <div className="topbar-actions">
          <button className="secondary-button compact" type="button" onClick={exportDraft}><DownloadSimple size={18} />备份草稿</button>
          <button className="primary-button compact" type="button" onClick={openExportCenter}><Export size={18} />导出 HTML</button>
        </div>
      </header>

      {(notice || error) ? <div className={error ? "notice error" : "notice"} role="status">
        {error || notice}<button type="button" onClick={() => { setNotice(""); setError(""); }}>关闭</button>
      </div> : null}

      <div className="editor-grid">
        <aside className="settings-panel">
          <section>
            <h2>影集信息</h2>
            <label><span>标题</span><input value={project.title} onChange={(event) => patchProject({ title: event.target.value })} /></label>
            <label><span>副标题</span><textarea rows={3} value={project.subtitle} onChange={(event) => patchProject({ subtitle: event.target.value })} /></label>
            <label><span>制作人</span><input value={project.author} placeholder="可选" onChange={(event) => patchProject({ author: event.target.value })} /></label>
            <div className="field-pair">
              <label><span>日期</span><input type="date" value={project.date} onChange={(event) => patchProject({ date: event.target.value })} /></label>
              <label><span>地点</span><input value={project.location} placeholder="例如：北海道" onChange={(event) => patchProject({ location: event.target.value })} /></label>
            </div>
            <label><span>同行人物</span><input value={project.people.join("，")} placeholder="用逗号分隔姓名" onChange={(event) => patchProject({ people: event.target.value.split(/[，,]/).map((item) => item.trim()).filter(Boolean) })} /></label>
          </section>
          <section className="reading-mode-section">
            <details className="reading-mode-disclosure" open>
              <summary className="reading-mode-summary"><span><strong>阅读方式</strong><small>{templates.length} 种方式，可随时切换</small></span><CaretDown size={17} /></summary>
              <div className="reading-mode-groups">
                <details className="reading-mode-group" open>
                  <summary><span>基础方式</span><small>{coreTemplates.length} 种</small><CaretDown size={14} /></summary>
                  <div className="template-list">
                    {coreTemplates.map((template) => <button key={template.id} type="button" className={project.template === template.id ? "template-option active" : "template-option"} onClick={() => patchProject({ template: template.id })}>
                      <span className="template-icon">{template.id === "gallery" || template.id === "deck" || template.id === "accordion" || template.id === "drift" ? <GridFour size={20} /> : template.id === "timeline" ? <HardDrive size={20} /> : template.id === "book" || template.id === "cinema" || template.id === "scrapbook" ? <BookOpen size={20} /> : <ImageSquare size={20} />}</span>
                      <span><strong>{template.name}</strong><small>{template.description}</small></span>
                    </button>)}
                  </div>
                </details>
                <details className="reading-mode-group experimental" open>
                  <summary><span><Sparkle size={14} /> 实验方式</span><small>{experimentalTemplates.length} 种</small><CaretDown size={14} /></summary>
                  <div className="template-list">
                    {experimentalTemplates.map((template) => <button key={template.id} type="button" className={project.template === template.id ? "template-option active" : "template-option"} onClick={() => patchProject({ template: template.id })}>
                      <span className="template-icon">{template.id === "circular" ? <Aperture size={20} /> : <Sparkle size={20} />}</span>
                      <span><strong>{template.name}</strong><small>{template.description}</small></span>
                    </button>)}
                  </div>
                </details>
              </div>
            </details>
          </section>
          <section>
            <h2>照片呈现</h2>
            <p className="section-hint">默认完整显示每张照片；边框只改变照片周围的纸张，不会裁掉内容。</p>
            <fieldset className="presentation-options">
              <legend>照片比例</legend>
              <div className="presentation-choice-row" role="group" aria-label="照片比例">
                {([["contain", "完整显示", "保留整张照片"], ["cover", "铺满画面", "允许裁切边缘"]] as const).map(([id, name, description]) => {
                  const active = (project.presentation?.fit ?? DEFAULT_MEDIA_PRESENTATION.fit) === id;
                  return <button key={id} type="button" className={`presentation-choice${active ? " active" : ""}`} aria-pressed={active} onClick={() => patchProject({ presentation: { ...(project.presentation ?? DEFAULT_MEDIA_PRESENTATION), fit: id as MediaFit } })}><strong>{name}</strong><small>{description}</small></button>;
                })}
              </div>
            </fieldset>
            <fieldset className="presentation-options">
              <legend>照片边框</legend>
              <div className="presentation-choice-row" role="group" aria-label="照片边框">
                {([["white", "白色纸边"], ["kraft", "牛皮纸"], ["color", "彩色纸边"], ["soft", "柔和阴影"], ["none", "无边框"]] as const).map(([id, name]) => {
                  const active = (project.presentation?.frame ?? DEFAULT_MEDIA_PRESENTATION.frame) === id;
                  return <button key={id} type="button" className={`presentation-chip${active ? " active" : ""}`} aria-pressed={active} onClick={() => patchProject({ presentation: { ...(project.presentation ?? DEFAULT_MEDIA_PRESENTATION), frame: id as FramePreset } })}>{name}</button>;
                })}
              </div>
            </fieldset>
            <fieldset className="presentation-options">
              <legend>纸边留白</legend>
              <div className="presentation-choice-row compact" role="group" aria-label="纸边留白">
                {([["compact", "紧凑"], ["regular", "标准"], ["wide", "宽松"]] as const).map(([id, name]) => {
                  const active = (project.presentation?.spacing ?? DEFAULT_MEDIA_PRESENTATION.spacing) === id;
                  return <button key={id} type="button" className={`presentation-chip${active ? " active" : ""}`} aria-pressed={active} onClick={() => patchProject({ presentation: { ...(project.presentation ?? DEFAULT_MEDIA_PRESENTATION), spacing: id as FrameSpacing } })}>{name}</button>;
                })}
              </div>
            </fieldset>
          </section>
          <section className="storage-info">
            <h2>本地存储</h2>
            <p>照片不会上传。清除浏览器数据会删除项目，请定期下载草稿。</p>
            <div className="recovery-card">
              <div className="recovery-row"><span>最后保存</span><strong>{formatSavedTime(project.updatedAt)}</strong></div>
              <div className="recovery-row"><span>项目内容</span><strong>{project.items.length} 张照片 · {project.title || "未命名影集"}</strong></div>
              {storage ? <>
                <div className="storage-meter" aria-label={`已使用 ${(storage.usage / storage.quota * 100).toFixed(1)}% 存储空间`}><span style={{ width: `${Math.min(100, storage.usage / storage.quota * 100)}%` }} /></div>
                <small>已使用约 {(storage.usage / 1024 / 1024).toFixed(0)} MB，可用配额约 {(storage.quota / 1024 / 1024 / 1024).toFixed(1)} GB</small>
                <small className={storage.persisted ? "storage-good" : "storage-caution"}>{storage.persisted ? "浏览器已标记为尽量保留" : "浏览器可能会清理站点数据"}</small>
              </> : <small>正在读取浏览器存储状态</small>}
              <div className="storage-actions">
                <button type="button" className="secondary-button compact" onClick={exportDraft} disabled={!project.items.length}><DownloadSimple size={15} />下载草稿</button>
                <button type="button" className="text-button compact" onClick={requestPersistence}>尽量保留</button>
                {saveState === "error" ? <button type="button" className="text-button compact" onClick={retrySave}>重试保存</button> : null}
              </div>
            </div>
          </section>
        </aside>

        <section className="photo-editor">
          <div className="photo-editor-head">
            <div><h2>照片与文字</h2><p>{project.items.length ? `${project.items.length} 张照片，拖动左侧手柄调整顺序。` : "先导入照片，再从一张照片开始讲述。"}</p></div>
            <button className="secondary-button compact" type="button" disabled={importing} onClick={() => fileInput.current?.click()}>
              <Plus size={17} />{importing ? "正在处理" : "导入照片"}
            </button>
            <input ref={fileInput} type="file" multiple accept="image/jpeg,image/png,image/webp" hidden onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.currentTarget.value = "";
              if (files.length) handleFiles(files);
            }} />
          </div>
          {importQueue.length ? <ImportQueuePanel tasks={importQueue} disabled={importing} onRetry={retryImport} onClear={() => setImportQueue([])} /> : null}
          {project.items.length ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={project.items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                <div className="photo-list">
                  {project.items.map((item, index) => <SortablePhoto key={item.id} item={item} index={index} url={thumbUrls[item.assetId]} isCover={project.coverAssetId === item.assetId} onChange={(patch) => updateItem(item.id, patch)} onCover={() => patchProject({ coverAssetId: item.assetId })} onRemove={() => removeItem(item)} />)}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <button className="drop-empty" type="button" onClick={() => fileInput.current?.click()} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); handleFiles(event.dataTransfer.files); }}>
              <ImageSquare size={38} /><strong>把照片放到这里</strong><span>支持 JPEG、PNG、WebP，可一次选择多张。</span>
            </button>
          )}
        </section>

        <aside className="preview-panel">
          <div className="preview-head"><span>实时预览</span><span>{templates.find((item) => item.id === project.template)?.name}</span></div>
          <div className="preview-scroll"><AlbumPreview document={document} urls={urls} /></div>
        </aside>
      </div>
      <nav className="mobile-editor-nav" aria-label="编辑器模式">
        <button type="button" className={mobileMode === "settings" ? "active" : ""} onClick={() => setMobileMode("settings")}>设置</button>
        <button type="button" className={mobileMode === "organize" ? "active" : ""} onClick={() => setMobileMode("organize")}>整理</button>
        <button type="button" className={mobileMode === "preview" ? "active" : ""} onClick={() => setMobileMode("preview")}>预览</button>
      </nav>
      {exportOpen ? <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setExportOpen(false)}>
        <section className="export-dialog" role="dialog" aria-modal="true" aria-labelledby="export-title">
          <div className="dialog-head"><div><span>生成与保存</span><h2 id="export-title">导出独立 HTML</h2></div><button className="icon-button" type="button" aria-label="关闭" onClick={() => setExportOpen(false)}><X size={20} /></button></div>
          <div className="export-summary"><div><span>项目</span><strong>{project.title}</strong></div><div><span>照片</span><strong>{project.items.length} 张</strong></div><div><span>模板</span><strong>{templates.find((item) => item.id === project.template)?.name}</strong></div><div><span>预计大小</span><strong>{estimatedBytes ? `${(estimatedBytes / 1024 / 1024).toFixed(1)} MB` : "正在计算"}</strong></div></div>
          <fieldset className="quality-options"><legend>图片质量</legend>
            {([
              ["light", "轻量", "最长边约 1600px，适合发送"],
              ["standard", "标准", "平衡画质与体积，推荐"],
              ["archive", "珍藏", "嵌入原图，文件可能很大"],
            ] as const).map(([id, name, description]) => <label key={id} className={project.exportQuality === id ? "quality-option active" : "quality-option"}><input type="radio" name="quality" checked={project.exportQuality === id} onChange={() => { patchProject({ exportQuality: id }); calculateEstimate(id); }} /><span><strong>{name}</strong><small>{description}</small></span></label>)}
          </fieldset>
          <p className="privacy-note">成品不请求网络、不含跟踪代码。即使 PhotoFolio 不再运行，基础照片与文字仍可阅读。</p>
          {estimatedBytes && estimatedBytes > 150 * 1024 * 1024 ? <p className="size-warning">文件超过 150 MB，建议改用标准质量或下载项目草稿长期保存。</p> : null}
          <div className="dialog-actions"><button className="secondary-button" type="button" onClick={() => setExportOpen(false)}>返回编辑</button><button className="primary-button" type="button" onClick={exportHtml}><Export size={18} />生成文件</button></div>
        </section>
      </div> : null}
    </main>
  );
}
